import { Link } from 'react-router-dom';
import {
  CalendarCheck2,
  ChevronRight,
  Coffee,
  Hourglass,
  LineChart,
  Stethoscope,
  Timer,
  UserRound,
  Wallet,
} from 'lucide-react';
import type { DashboardUpNextDto, DoctorOverviewDto } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { cn, timeOnly } from '@/lib/utils';
import { formatMinutes, queueStages, sumBy } from './dashboard-math';
import { Sparkline, TrendChart } from './dashboard-charts';
import {
  EmptyChart,
  formatCount,
  KpiCard,
  moneyWhole,
  Panel,
  PanelLink,
  TONE_COLOR,
} from './dashboard-ui';

const UP_NEXT_STATUS: Record<string, { labelKey: string; className: string }> = {
  in_consultation: { labelKey: 'dashboard_up_next_with_you', className: 'bg-chart-3/15 text-chart-3' },
  arrived: { labelKey: 'dashboard_stage_waiting', className: 'bg-chart-4/15 text-chart-4' },
  confirmed: { labelKey: 'dashboard_up_next_confirmed', className: 'bg-chart-2/15 text-chart-2' },
  booked: { labelKey: 'dashboard_up_next_expected', className: 'bg-muted text-muted-foreground' },
};

export function DoctorOverview({
  data,
  indexOffset = 0,
}: {
  data: DoctorOverviewDto;
  indexOffset?: number;
}) {
  const stages = queueStages(data.appointments);
  const count = (key: string) => stages.find((stage) => stage.key === key)?.count ?? 0;
  const waiting = count('waiting');
  const seen = count('seen');
  const expected = count('expected');
  const fortnight = sumBy(data.days, (day) => day.consultations);
  const activeDays = data.days.filter((day) => day.consultations > 0).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <KpiCard
          index={indexOffset}
          label={tr('admin', 'dashboard_consultations_today')}
          icon={Stethoscope}
          tone="primary"
          value={data.encounters}
          caption={tr('admin', 'dashboard_completed_still_expected', { seen, expected })}
          chart={
            <Sparkline
              data={data.days.map((day) => day.consultations)}
              color={TONE_COLOR.primary}
            />
          }
        />
        <KpiCard
          index={indexOffset + 1}
          label={tr('admin', 'dashboard_waiting_for_you')}
          icon={Hourglass}
          tone="amber"
          value={waiting}
          caption={
            waiting
              ? tr('admin', 'dashboard_arrived_ready_to_be_seen')
              : tr('admin', 'dashboard_nobody_waiting_now')
          }
          to="/queue"
        />
        <KpiCard
          index={indexOffset + 2}
          label={tr('admin', 'dashboard_average_consult')}
          icon={Timer}
          tone="violet"
          value={data.averageConsultMinutes}
          format={(value) => formatMinutes(value)}
          caption={tr('admin', 'dashboard_opened_to_closed_today')}
        />
        <KpiCard
          index={indexOffset + 3}
          label={tr('admin', 'dashboard_my_share_today')}
          icon={Wallet}
          tone="green"
          value={data.revenueShareMinor}
          format={moneyWhole}
          caption={tr('admin', 'dashboard_on_what_patients_paid')}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          index={indexOffset + 4}
          className="lg:col-span-2"
          title={tr('admin', 'dashboard_my_consultations')}
          subtitle={
            activeDays
              ? tr('admin', 'dashboard_my_consultations_hint', {
                  total: formatCount(fortnight),
                  average: Math.round(fortnight / activeDays),
                })
              : tr('admin', 'dashboard_last_14_days')
          }
          icon={LineChart}
        >
          <TrendChart
            data={data.days}
            series={[{ key: 'consultations', label: tr('admin', 'dashboard_consultations'), color: TONE_COLOR.primary }]}
            height={220}
          />
        </Panel>

        <Panel
          index={indexOffset + 5}
          title={tr('admin', 'dashboard_up_next')}
          subtitle={tr('admin', 'dashboard_up_next_hint')}
          icon={CalendarCheck2}
          tone="sky"
          action={<PanelLink to="/queue">{tr('admin', 'dashboard_my_queue')}</PanelLink>}
        >
          <UpNext rows={data.upNext} />
        </Panel>
      </div>
    </div>
  );
}

function UpNext({ rows }: { rows: DashboardUpNextDto[] }) {
  if (rows.length === 0) {
    return (
      <EmptyChart className="min-h-[12rem]">
        <span className="flex flex-col items-center gap-2">
          <Coffee className="h-6 w-6 text-primary" aria-hidden />
          {tr('admin', 'dashboard_nobody_else_booked')}
        </span>
      </EmptyChart>
    );
  }
  return (
    <ul className="-mx-2 space-y-1">
      {rows.map((row, index) => {
        const status = UP_NEXT_STATUS[row.status] ?? UP_NEXT_STATUS.booked;
        return (
          <li
            key={row.appointmentId}
            className="duration-500 animate-in fade-in-0 slide-in-from-right-2 fill-mode-backwards"
            style={{ animationDelay: `${200 + index * 60}ms` }}
          >
            <Link
              to="/queue"
              className="group flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-accent/60 focus-ring"
            >
              <span className="grid h-9 w-11 shrink-0 place-items-center rounded-md bg-muted text-xs font-semibold tabular-nums">
                {row.tokenNo ?? <UserRound className="h-4 w-4 text-muted-foreground" aria-hidden />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-medium">{row.patientName}</span>
                  {row.priority !== 'routine' && (
                    <span className="shrink-0 rounded bg-destructive/10 px-1 text-[10px] font-semibold uppercase text-destructive">
                      {row.priority}
                    </span>
                  )}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {timeOnly(row.slotAt)}
                  {row.mrn ? ` · ${row.mrn}` : ''}
                </span>
              </span>
              <span
                className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium',
                  status.className,
                )}
              >
                {tr('admin', status.labelKey)}
              </span>
              <ChevronRight
                className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 rtl:rotate-180"
                aria-hidden
              />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
